/**
 * Thin auth layer mirroring apps/dashboard_api/auth.py:
 *
 * - VITE_AUTH_MODE=dev: no login. Requests carry X-Mandant-Id (default
 *   "strawa", override via VITE_DEV_MANDANT_ID). SSE uses ?token=<mandant>.
 * - otherwise (keycloak, default): keycloak-js with login-required.
 *   Requests carry Authorization: Bearer <jwt>; EventSource cannot send
 *   headers, so the client trades the JWT for a single-use ticket via
 *   POST /api/v1/events/ticket and opens the stream with ?ticket=. The JWT
 *   never appears in a URL (access-log leak, WT4 2026-07-03).
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { initKeycloak } from "./keycloakRuntime";

export type AuthMode = "dev" | "keycloak";

export interface AuthContextValue {
  mode: AuthMode;
  ready: boolean;
  mandantId: string;
  /** Stable Keycloak subject, when a signed-in identity is available. */
  subject: string | null;
  /** Headers for fetch() calls against the API. */
  /** Async, weil im Keycloak-Betrieb vor jeder Anfrage ein abgelaufenes
   *  Zugriffstoken erneuert werden muss. */
  headers: () => Promise<Record<string, string>>;
  /**
   * Build a full EventSource URL for an SSE path. In keycloak mode this
   * fetches a fresh single-use ticket per call, so call it again on every
   * (re)connect.
   */
  sseUrl: (path: string) => Promise<string>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function resolveMode(): AuthMode {
  return import.meta.env.VITE_AUTH_MODE === "dev" ? "dev" : "keycloak";
}

export interface KeycloakLike {
  token?: string;
  tokenParsed?: Record<string, unknown>;
  init(options: Record<string, unknown>): Promise<boolean>;
  updateToken(minValidity: number): Promise<boolean>;
  logout(): void;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const mode = resolveMode();
  const [ready, setReady] = useState(mode === "dev");
  const [keycloak, setKeycloak] = useState<KeycloakLike | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "dev") {
      return;
    }
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setInterval> | undefined;
    initKeycloak()
      .then((kc) => {
        if (cancelled) {
          return;
        }
        setKeycloak(kc);
        setReady(true);
        refreshTimer = setInterval(() => {
          kc.updateToken(30).catch(() => kc.logout());
        }, 20_000);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Anmeldung fehlgeschlagen. Keycloak nicht erreichbar.");
        }
      });
    return () => {
      cancelled = true;
      if (refreshTimer !== undefined) {
        clearInterval(refreshTimer);
      }
    };
  }, [mode]);

  const value = useMemo<AuthContextValue>(() => {
    if (mode === "dev") {
      const mandantId = import.meta.env.VITE_DEV_MANDANT_ID || "strawa";
      return {
        mode,
        ready: true,
        mandantId,
        subject: null,
        headers: async () => ({ "X-Mandant-Id": mandantId }),
        // auth.py dev mode reads the mandant_id from ?token= for SSE.
        sseUrl: async (path: string) =>
          `${path}?token=${encodeURIComponent(mandantId)}`,
        logout: () => {
          // No session in dev mode.
        },
      };
    }
    const freshToken = async (): Promise<string | undefined> => {
      if (!keycloak) {
        return undefined;
      }
      try {
        await keycloak.updateToken(30);
      } catch {
        // Kein Abmelden hier. Die Erneuerung scheitert auch bei einer kurzen
        // Netzstoerung, und beim Laden der Huelle laufen fuenf Anfragen
        // parallel; ein Fehlschlag wuerde den Bediener sofort rauswerfen.
        // Das vorhandene Token wird weiter mitgeschickt: gilt es noch, geht
        // die Anfrage durch, sonst kommt eine 401. Das endgueltige Abmelden
        // bleibt beim 20-Sekunden-Takt, der eine echt abgelaufene Sitzung
        // erkennt.
      }
      return keycloak.token;
    };
    const mandantClaim = keycloak?.tokenParsed?.["mandant_id"];
    const subjectClaim = keycloak?.tokenParsed?.["sub"];
    return {
      mode,
      ready,
      mandantId: typeof mandantClaim === "string" ? mandantClaim : "",
      subject: typeof subjectClaim === "string" ? subjectClaim : null,
      // Vor jeder Anfrage erneuern statt nur alle 20 Sekunden im Takt.
      // updateToken(30) geht nicht ins Netz, solange das Token noch laenger
      // als 30 Sekunden gilt. Ohne diesen Schritt gingen nach einer Pause
      // des Browsers die ersten Anfragen mit abgelaufenem Token raus und
      // kamen als 401 zurueck, bis der Takt sie einholte.
      headers: async (): Promise<Record<string, string>> => {
        const token = await freshToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
      sseUrl: async (path: string): Promise<string> => {
        const token = await freshToken();
        const response = await fetch("/api/v1/events/ticket", {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error(`SSE-Ticket fehlgeschlagen (${response.status})`);
        }
        const { ticket } = (await response.json()) as { ticket: string };
        return `${path}?ticket=${encodeURIComponent(ticket)}`;
      },
      logout: () => keycloak?.logout(),
    };
  }, [mode, ready, keycloak]);

  if (error) {
    return (
      <div className="empty-state">
        <p className="error-text">{error}</p>
      </div>
    );
  }

  if (!value.ready) {
    return (
      <div className="empty-state">
        <p>Anmeldung läuft…</p>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
